import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { Loader2, Globe, Moon, Sun } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useTheme } from 'next-themes';

const SuperAdminLogin = () => {
  const { user, role } = useAuth();
  const { lang, setLang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user && role === 'super_admin') {
    return <Navigate to="/super-admin" replace />;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setLoading(true);
    try {
      await supabase.auth.signOut();

      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        toast({ title: lang === 'ar' ? 'بيانات الدخول غير صحيحة' : 'Invalid credentials', description: error.message, variant: 'destructive' });
        return;
      }

      const { data: { user: signedUser } } = await supabase.auth.getUser();
      if (!signedUser) {
        toast({ title: lang === 'ar' ? 'خطأ في المصادقة' : 'Authentication error', variant: 'destructive' });
        return;
      }

      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', signedUser.id)
        .maybeSingle();

      if (roleData?.role !== 'super_admin') {
        await supabase.auth.signOut();
        toast({
          title: lang === 'ar' ? 'غير مصرح' : 'Unauthorized',
          description: lang === 'ar' ? 'هذه الصفحة مخصصة للمدير الخارق فقط' : 'This page is for super admins only',
          variant: 'destructive',
        });
        return;
      }

      navigate('/super-admin', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const t = lang === 'ar';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[hsl(174,40%,92%)] to-[hsl(174,35%,82%)] dark:from-[hsl(174,30%,15%)] dark:to-[hsl(174,25%,10%)] p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-xl font-semibold text-foreground">
              {t ? 'لوحة التحكم' : 'Control Panel'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t ? 'تسجيل الدخول للوحة إدارة المنصة' : 'Sign in to the platform management panel'}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">{t ? 'البريد الإلكتروني' : 'Email'}</label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                dir="ltr"
                placeholder="admin@example.com"
                className="bg-white/60 dark:bg-white/5 border-border"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">{t ? 'كلمة المرور' : 'Password'}</label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                dir="ltr"
                className="bg-white/60 dark:bg-white/5 border-border"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[hsl(174,50%,40%)] hover:bg-[hsl(174,50%,35%)] text-white"
              disabled={loading}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin me-2" />{t ? 'جاري التحقق...' : 'Verifying...'}</>
              ) : (
                t ? 'تسجيل الدخول' : 'Sign In'
              )}
            </Button>
          </form>
        </div>

        {/* Bottom controls */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          >
            <Globe className="h-4 w-4 me-1" />
            {lang === 'ar' ? 'English' : 'العربية'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 me-1" /> : <Moon className="h-4 w-4 me-1" />}
            {theme === 'dark' ? (t ? 'فاتح' : 'Light') : (t ? 'داكن' : 'Dark')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
